import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { montarResumoCurto, montarResumoProntuario, interpretarTotal, rotuloEscolaridade, rotuloReferencia, formatarDataHora } from '../../lib/report';
import { DOMAINS_CONFIG, REFERENCE_OPTIONS } from '../../lib/calculator';
import { RULER_GROUPS, batchShare, cellState, groupPartial, itemKey } from '../../lib/guidedFlow';
import { ResultRuler } from './ScoreRuler';
import NormativeReading from './NormativeReading';
import OptionCards from './OptionCards';
import PrintSheet from './PrintSheet';
import SaveToPatient from './SaveToPatient';
import DoutoFeatureCard from '../DoutoFeatureCard';

/** Mesmo texto de ResultModal.jsx: estado sempre em texto, nunca só cor/ícone. */
function ItemState({ value }) {
  if (value === true) return <span className="font-medium text-[var(--accent)]">Acertou · 1</span>;
  if (value === false) return <span className="font-medium text-[var(--miss)]">Errou · 0</span>;
  return <span className="text-[var(--na-text)]">Não avaliado</span>;
}

/**
 * Página de interpretação completa de uma avaliação (modal enxuto só responde 4
 * perguntas; aqui fica tudo que o profissional pode querer revisar com calma).
 *
 * `snapshot` vem de src/lib/resultSnapshot.js. `snapshot.checked`/`blockScores` só
 * existem para avaliações calculadas nesta aba (fonte 'local') — uma avaliação salva no
 * histórico ('saved') só tem o escore por domínio, e a página mostra isso explicitamente
 * em vez de fingir um detalhamento que o registro não guarda.
 */
export default function AvaliacaoResultado({ snapshot }) {
  const { source, total, totalMax, mode, education, reference: savedReference, ageConfirm: savedAgeConfirm, unevaluated, checked, batch, blockScores, domainScores, createdAt } = snapshot;
  const domains = mode === 'domains';
  const hasItems = domains && Boolean(checked);

  const [reference, setReference] = useState(savedReference);
  const [ageConfirm, setAgeConfirm] = useState(savedAgeConfirm);
  const [textoEditado, setTextoEditado] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [saveSignal, setSaveSignal] = useState(0);

  const { texto: interpretacao } = interpretarTotal({ total, education, reference, ageConfirm });
  const aplicadoEm = source === 'local' && createdAt ? formatarDataHora(new Date(createdAt)) : '';
  const partials = hasItems ? RULER_GROUPS.map((group) => ({ group, ...groupPartial(checked, group) })) : [];
  const erros = hasItems ? Object.values(checked).filter((v) => v === false).length : 0;
  const acertos = hasItems ? Object.values(checked).filter((v) => v === true).length : total;

  const resumoAuto = montarResumoCurto({
    total,
    grupos: hasItems ? partials.map(({ group, hits, max }) => ({ copy: group.copy, hits, max })) : [],
    education,
    referencia: null,
    naoAvaliados: unevaluated || 0,
    emBloco: hasItems && batch ? batchShare(batch) > 0.8 : false,
    aplicadoEm
  });
  const textoAtual = textoEditado ?? resumoAuto;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(textoAtual);
    } catch {
      window.prompt('Copie o resumo abaixo:', textoAtual);
    }
    window.eidocAnalytics?.capture('assessment_result_copied', { source: 'avaliacao' });
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  }

  return (
    <main className="mx-auto max-w-[840px] px-4 py-8 md:py-12">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--line)] pb-4">
        <div>
          <Link to="/" onClick={() => window.eidocAnalytics?.capture('assessment_new_started', { source: 'avaliacao' })} className="text-[length:var(--fs-14)] font-medium text-[var(--accent)] underline">← Nova avaliação</Link>
          <h1 className="mt-2 text-[length:var(--fs-24)] font-semibold text-[var(--ink)]">Interpretação da avaliação</h1>
        </div>
        <p className="font-display text-[length:var(--fs-40)] font-semibold leading-none text-[var(--ink)]" style={{ fontVariantNumeric: 'tabular-nums' }} aria-label={`${total} de ${totalMax}`}>
          {total} <span className="text-[length:var(--fs-20)] text-[var(--ink-2)]">de {totalMax}</span>
        </p>
      </div>

      <section aria-labelledby="resultado-geral-titulo" className="mt-6">
        <h2 id="resultado-geral-titulo" className="text-[length:var(--fs-18)] font-semibold text-[var(--ink)]">Resultado geral</h2>
        <dl className="mt-2 flex flex-wrap gap-x-8 gap-y-1 text-[length:var(--fs-15)]">
          <div className="flex items-baseline gap-2"><dt className="text-[var(--ink-2)]">Escolaridade</dt><dd className="font-medium">{rotuloEscolaridade(education)}</dd></div>
          {source === 'local' && domains && (
            <>
              <div className="flex items-baseline gap-2"><dt className="text-[var(--ink-2)]">Itens avaliados</dt><dd className="mono font-medium">{totalMax - (unevaluated || 0)}/{totalMax}</dd></div>
              <div className="flex items-baseline gap-2"><dt className="text-[var(--ink-2)]">Não avaliados</dt><dd className="mono font-medium">{unevaluated || 0}</dd></div>
            </>
          )}
        </dl>
        {hasItems && <div className="mt-4"><ResultRuler checked={checked} /></div>}
        {domains && unevaluated > 0 && (
          <p role="status" className="mt-2 text-[length:var(--fs-14)] text-[var(--ink)]">
            {unevaluated} {unevaluated === 1 ? 'item não avaliado' : 'itens não avaliados'} — o total pode estar subestimado.{' '}
            <Link to="/meem-incompleto" className="font-medium text-[var(--accent)] underline">Como registrar e interpretar uma aplicação incompleta</Link>.
          </p>
        )}
      </section>

      {domains && (
        <section aria-labelledby="dominios-titulo" className="mt-8 border-t border-[var(--line)] pt-6">
          <h2 id="dominios-titulo" className="text-[length:var(--fs-18)] font-semibold text-[var(--ink)]">Pontuação por domínio</h2>
          <table className="data-table mt-3 w-full max-w-[560px]">
            <caption className="sr-only">Pontos por domínio</caption>
            <thead><tr><th>Domínio</th><th className="text-right">Obtido</th><th className="text-right">Máximo</th></tr></thead>
            <tbody>
              {hasItems
                ? partials.map(({ group, hits, max }) => (
                  <tr key={group.id}><th scope="row">{group.label}</th><td className="mono text-right">{hits}</td><td className="mono text-right text-[var(--ink-2)]">{max}</td></tr>
                ))
                : DOMAINS_CONFIG.map((domain) => (
                  <tr key={domain.id}><th scope="row">{domain.label}</th><td className="mono text-right">{domainScores?.[domain.id] ?? '—'}</td><td className="mono text-right text-[var(--ink-2)]">{domain.max}</td></tr>
                ))}
            </tbody>
          </table>

          {hasItems ? (
            <details className="mt-3">
              <summary className="inline-flex min-h-11 cursor-pointer items-center text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline">Ver todos os itens por domínio</summary>
              <div className="grid gap-4 pb-2 pt-2">
                {RULER_GROUPS.map((group) => (
                  <div key={group.id}>
                    <h3 className="text-[length:var(--fs-15)] font-semibold">{group.label} <span className="mono font-normal text-[var(--ink-2)]">{groupPartial(checked, group).hits}/{group.max}</span></h3>
                    {group.blocks.map((block) => (
                      <div key={block.id} className="mt-1">
                        {group.blocks.length > 1 && <p className="text-[length:var(--fs-14)] text-[var(--ink-2)]">{block.label}</p>}
                        <ul>
                          {block.items.map((item, i) => {
                            const key = itemKey(block.id, i);
                            return (
                              <li key={key} className="flex flex-wrap items-baseline justify-between gap-x-4 border-b border-[var(--line)] py-1.5 text-[length:var(--fs-15)] last:border-b-0" data-item-state={cellState(checked, key)}>
                                <span className="min-w-0">{item.label}{batch?.[key] && <span className="ml-2 text-[length:var(--fs-13)] text-[var(--ink-2)]">em lote</span>}</span>
                                <ItemState value={checked[key]} />
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </details>
          ) : (
            <p className="mt-2 max-w-[62ch] text-[length:var(--fs-14)] text-[var(--ink-2)]">
              Esta avaliação já está no histórico: o registro guarda a pontuação por domínio, mas não o detalhamento item a item nem a leitura por bloco em relação a pessoas saudáveis — isso só fica disponível no momento do cálculo, antes de salvar.
            </p>
          )}
        </section>
      )}

      <section aria-labelledby="interpretacao-titulo" className="mt-8 border-t border-[var(--line)] pt-6">
        <h2 id="interpretacao-titulo" className="text-[length:var(--fs-18)] font-semibold text-[var(--ink)]">Interpretação clínica</h2>
        <p className="mt-2 max-w-[65ch] text-[length:var(--fs-16)] leading-relaxed text-[var(--ink)]">{interpretacao}</p>
        <p className="mt-2 text-[length:var(--fs-14)] text-[var(--ink-2)]">
          Escolaridade informada: <strong className="font-semibold text-[var(--ink)]">{rotuloEscolaridade(education)}</strong>. Referência: <strong className="font-semibold text-[var(--ink)]">{rotuloReferencia(reference)}</strong>.
        </p>
        <details className="mt-2 text-[length:var(--fs-15)]">
          <summary className="inline-flex min-h-11 cursor-pointer items-center font-medium text-[var(--accent)] underline">Trocar a referência de interpretação</summary>
          <div className="grid gap-5 pt-2">
            <OptionCards legend="Referência de interpretação" name="avaliacao-referencia" options={REFERENCE_OPTIONS} value={reference} onChange={setReference} />
            {reference === 'lourenco' && (
              <fieldset>
                <legend className="mb-2 font-semibold text-[var(--ink)]">A pessoa avaliada tem mais de 65 anos?</legend>
                <div className="flex flex-wrap gap-2">
                  {[['yes', 'Sim'], ['no', 'Não'], ['not_informed', 'Não informado']].map(([value, label]) => (
                    <button key={value} type="button" aria-pressed={ageConfirm === value} onClick={() => setAgeConfirm(value)}
                      className={`min-h-11 rounded-[6px] border px-4 font-medium ${ageConfirm === value ? 'border-[var(--accent)] bg-[var(--accent-tint)] text-[var(--accent)]' : 'border-[var(--cell-border)] text-[var(--ink)] hover:bg-[var(--hover)]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}
          </div>
        </details>
        <p className="mt-4 max-w-[65ch] text-[length:var(--fs-14)] leading-relaxed text-[var(--ink-2)]">
          Instrumento de rastreio cognitivo. A pontuação isolada não estabelece diagnóstico, não identifica a causa de um declínio e não substitui avaliação clínica, funcional e, quando indicada, complementar. Escolaridade, déficit sensorial, dor, fadiga, delirium, depressão e fármacos alteram o resultado.
        </p>
        {domains && hasItems && (
          <details className="mt-4">
            <summary className="inline-flex min-h-11 cursor-pointer items-center text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline">Leitura por bloco em relação a pessoas saudáveis</summary>
            <NormativeReading blockScores={blockScores} />
          </details>
        )}
      </section>

      <section aria-labelledby="prontuario-titulo" className="mt-8 border-t border-[var(--line)] pt-6">
        <h2 id="prontuario-titulo" className="text-[length:var(--fs-18)] font-semibold text-[var(--ink)]">Texto para prontuário</h2>
        <label htmlFor="avaliacao-texto-prontuario" className="mt-1 block text-[length:var(--fs-14)] text-[var(--ink-2)]">Você pode editar. O texto fica só neste navegador. Não inclua nome nem dados que identifiquem o paciente.</label>
        <textarea id="avaliacao-texto-prontuario" rows={4} value={textoAtual} onChange={(e) => setTextoEditado(e.target.value)} className="input-field mt-1 w-full text-[length:var(--fs-14)]" />
        {textoEditado !== null && <button type="button" onClick={() => setTextoEditado(null)} className="mt-1 min-h-11 text-[length:var(--fs-15)] font-medium text-[var(--accent)] underline">Restaurar o texto automático</button>}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={copiar} className="btn-primary">Copiar para o prontuário</button>
          <button type="button" onClick={() => window.print()} className="btn-secondary">Imprimir</button>
          <p role="status" aria-live="polite" className="text-[length:var(--fs-14)] text-[var(--ink-2)]">{copiado ? 'Copiado' : ''}</p>
        </div>
      </section>

      <section aria-labelledby="educativo-titulo" className="mt-8 border-t border-[var(--line)] pt-6">
        <h2 id="educativo-titulo" className="text-[length:var(--fs-18)] font-semibold text-[var(--ink)]">Para entender melhor este resultado</h2>
        <ul className="mt-2 grid gap-1 text-[length:var(--fs-15)]">
          <li><Link to="/escolaridade" className="font-medium text-[var(--accent)] underline">Mini Mental por escolaridade</Link> — como a escolaridade muda a leitura do resultado.</li>
          <li><Link to="/pontos-de-corte" className="font-medium text-[var(--accent)] underline">Ponto de corte do Mini Mental</Link> — por que mediana não é ponto de corte diagnóstico.</li>
          <li><Link to="/interpretacao-dos-resultados" className="font-medium text-[var(--accent)] underline">Como interpretar o resultado</Link> — limites do instrumento e quando investigar mais.</li>
        </ul>
      </section>

      {source === 'local' && (
        <section aria-label="Guardar no histórico" className="mt-8 border-t border-[var(--line)] pt-6">
          <SaveToPatient variant="hosted" startSignal={saveSignal} total={total} education={education} reference={reference} ageConfirm={ageConfirm} blockScores={blockScores} mode={mode} />
          {saveSignal === 0 && <button type="button" onClick={() => setSaveSignal((n) => n + 1)} className="btn-secondary">Guardar no histórico do paciente</button>}
        </section>
      )}

      <div className="mt-10">
        <DoutoFeatureCard placement="interpretacao_completa" />
      </div>

      {/* Nota discreta do mesmo autor, depois de todo o resultado. Link sem nenhum dado da avaliação. */}
      <p className="no-print mt-6 max-w-[65ch] text-[length:var(--fs-14)] leading-relaxed text-[var(--ink-2)]" data-linksdoctor-cta>
        Também atende em consultório? O{' '}
        <a href="https://links.doctor/?utm_source=minimental&utm_medium=calculadora&utm_campaign=linksdoctor-cta" target="_blank" rel="nofollow noopener" className="font-medium text-[var(--ink)] underline underline-offset-2">links.doctor</a>
        {' '}junta WhatsApp, endereço, agendamento e CRM numa página que o paciente abre pelo celular. 30 dias grátis.
      </p>

      <PrintSheet total={total} education={education} reference={reference} ageConfirm={ageConfirm} checked={checked} batch={batch || {}} blockScores={blockScores} mode={mode} unevaluated={unevaluated || 0} />
    </main>
  );
}
